import React, { useCallback, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CheckmarkOutline, NucleoFolderPlus, NucleoMagnifier } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { BaseMenuItem, Box, Popover, Stack, Tooltip, Typography } from '@lyearn/ui';
import { Link } from '@/components/Link';
import Loader from '@/components/Loader';
import { SearchBar } from '@/components/SearchBar';
import VirtualizedList from '@/components/VirtualizedList';
import FileEntity from '@/modules/tasks/file.entity';
import { getFileIcon } from '@/modules/tasks/utils/getFileIcon';
import { FileMinifiedFragment } from '@/modules/tasks/views/TasksDashboard/graphql/fragments/generated/fileMinified';
import routes from '@/pages/corporate/routes';

import { useActionItemContext } from '../../context';
import { FILE_SEARCH_PROJECT_VARIABLES, useFileSearch } from '../../hooks/useFileSearch';

const DEFAULT_PAGE_SIZE = 10;

const ActionProject = () => {
  const { t } = useTranslation('Clients');
  const { actionItem, actions, disableInteractiveTooltip = false } = useActionItemContext();
  const isReadOnly = actionItem.parentActionItem?.id; //subtasks

  const [isSearchOpen, { on: openSearch, off: closeSearch }] = useToggle(false);
  const [search, setSearch] = useState('');
  const [isMenuOpen, { on, off: hideMenu, toggle }] = useToggle(false);

  const {
    files: projects,
    totalCount,
    fetchMore,
    isFetchingMore,
  } = useFileSearch({
    pageSize: DEFAULT_PAGE_SIZE,
    filters: FILE_SEARCH_PROJECT_VARIABLES,
    search,
  });

  const selectedFile = useMemo(() => {
    return actionItem.file && FileEntity.isFileTypeProject(actionItem.file)
      ? actionItem.file
      : null;
  }, [actionItem.file]);

  const onFileSelect = useCallback(
    (file: FileMinifiedFragment) => {
      actions?.onFileChange?.(file.id, actionItem.id);
      hideMenu();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions, actionItem.id],
  );

  const onToggle = useCallback(
    (open?: boolean) => {
      toggle(open);
      return;
    },
    [toggle],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const renderButton = () => {
    return (
      <Stack alignItems="center" direction="row" gapX={8}>
        <Stack className="flex-shrink-0 ">
          {selectedFile ? (
            getFileIcon(selectedFile, 18, 'text-icon-primary')
          ) : (
            <Tooltip disableInteractive={disableInteractiveTooltip} title={t('Clients:AddProject')}>
              <span>
                <NucleoFolderPlus fillColorToken="icon-placeholder" height={18} width={18} />
              </span>
            </Tooltip>
          )}
        </Stack>
        {selectedFile && (
          <Link className={cn('min-w-0 flex-1')} to={routes.ProjectsDashboard(selectedFile?.id)}>
            <TypographyWithTooltip
              disableInteractive={disableInteractiveTooltip}
              numberOfLines={1}
              variant="body-short-02">
              {selectedFile?.name}
            </TypographyWithTooltip>
          </Link>
        )}
      </Stack>
    );
  };

  const shouldShowSearch = useMemo(
    () => isSearchOpen || search || projects.length >= 5,
    [isSearchOpen, search, projects.length],
  );

  const renderHeader = useCallback(() => {
    // Show search bar if: there's an active search, search is open, or there are 5+ items
    if (!shouldShowSearch) return null;

    return (
      <Stack className="px-12 py-8 border-b border-border" direction="col" gap={0}>
        <SearchBar
          className="!me-0 h-32 !min-h-[3.2rem]"
          placeholder={t('Common:Search')}
          shape="square"
          size="medium"
          value={search}
          onChange={handleSearchChange}
        />
        {search && (
          <Typography className="mt-4 px-4" color="text-text-secondary" variant="body-short-03">
            {t('Common:NResults', { count: totalCount || 0 })}
          </Typography>
        )}
      </Stack>
    );
  }, [shouldShowSearch, search, totalCount, t, handleSearchChange]);

  const renderFileItem = useCallback(
    (index: number) => {
      const file = projects[index];
      if (!file) return null;

      const isSelected = selectedFile?.id === file.id;

      return (
        <Box className="px-8">
          <BaseMenuItem
            className={cn(
              'my-2 flex w-full items-center justify-between rounded-8 py-8 ps-0 pe-12 space-s-12',
            )}
            onClick={() => onFileSelect(file as FileMinifiedFragment)}>
            <Stack alignItems="center" className="w-[86%]">
              <Stack
                alignItems="center"
                className={cn(
                  'me-2 ms-12',
                  isSelected ? 'text-icon-active' : 'text-icon-secondary',
                )}
                justifyContent="center">
                {getFileIcon(file, 20, isSelected ? 'text-icon-active' : 'text-icon-secondary')}
              </Stack>
              <Typography
                className="xs:body-short-01 truncate ms-12"
                color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
                variant="body-short-02">
                {file.name}
              </Typography>
            </Stack>
            {isSelected && <CheckmarkOutline className="h-16 w-16 flex-shrink-0" />}
          </BaseMenuItem>
        </Box>
      );
    },
    [projects, selectedFile, onFileSelect],
  );

  if (isReadOnly) {
    return (
      <Stack className={cn('-ml-8 rounded-8 cursor-not-allowed p-8 w-full bg-transparent')}>
        {renderButton()}
      </Stack>
    );
  }

  return (
    <Stack
      className={cn(
        'border bg-transparent border-transparent rounded-8 hover:border-border active:border-primary w-full cursor-pointer',
        selectedFile ? 'px-8 py-8' : 'py-10 px-8',
      )}>
      <Popover open={isMenuOpen && !selectedFile} onOpenChange={onToggle}>
        {({ open }) => (
          <>
            <Popover.Button className="w-full" disabled={false}>
              {renderButton()}
            </Popover.Button>
            {open ? (
              <Popover.Content
                className="max-w-[294px]"
                minWidth={29.4}
                position="bottom-start"
                useAnchorElWidth={true}>
                {renderHeader()}
                <Box className="h-[256px] overflow-auto">
                  {projects.length > 0 ? (
                    <VirtualizedList
                      Header={null}
                      ListFooterComponent={isFetchingMore ? <Loader showLabel={false} /> : null}
                      data={projects}
                      endReached={fetchMore}
                      itemContent={renderFileItem}
                    />
                  ) : (
                    <Stack className="flex items-center w-full justify-center h-full">
                      <Typography
                        className="h-full flex justify-center items-center"
                        color="text-text-placeholder"
                        variant="body-short-02">
                        <div className="flex items-center flex-col gap-y-10 justify-center w-full h-full">
                          <NucleoMagnifier
                            className="h-24 w-24"
                            fillColorToken="icon-placeholder"
                          />
                          <span className="text-text-paragraph font-medium">
                            {t('Common:NoResultsFound')}
                          </span>
                          <div className="text-text-placeholder flex justify-center items-center text-center px-10">
                            {t('Common:NoResultsFoundDescription')}
                          </div>
                        </div>
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Popover.Content>
            ) : null}
          </>
        )}
      </Popover>
    </Stack>
  );
};

export default React.memo(ActionProject);
